import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2, Bot, User, GripVertical, Move, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm Jiffys Assistant. I can help you with:\n• Finding available service providers\n• Booking roadside assistance or mechanic services\n• Navigating the app\n• Answering questions about pricing\n\nHow can I help you today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Draggable state
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('ai_chat_position');
    return saved ? JSON.parse(saved) : null;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const widgetRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Save position
  const savePosition = useCallback((pos) => {
    localStorage.setItem('ai_chat_position', JSON.stringify(pos));
  }, []);
  
  // Reset position
  const resetPosition = () => {
    setPosition(null);
    localStorage.removeItem('ai_chat_position');
  };
  
  // Drag handlers
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    
    const rect = widgetRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({ x: clientX - rect.left, y: clientY - rect.top });
    }
    setIsDragging(true);
  }, []);
  
  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    
    const rect = widgetRef.current?.getBoundingClientRect();
    const maxX = window.innerWidth - (rect?.width || 384);
    const maxY = window.innerHeight - (rect?.height || 500);
    
    const newX = Math.max(0, Math.min(clientX - dragOffset.x, maxX));
    const newY = Math.max(0, Math.min(clientY - dragOffset.y, maxY));
    
    setPosition({ x: newX, y: newY });
  }, [isDragging, dragOffset]);
  
  const handleDragEnd = useCallback(() => {
    if (isDragging && position) {
      setIsDragging(false);
      savePosition(position);
    }
  }, [isDragging, position, savePosition]);
  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/assistant`, {
        message: userMessage,
        context: 'customer_support'
      });
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        providers: response.data.providers,
        services: response.data.services
      }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I'm having trouble connecting. Please try again or call us at (260) 218-3536 for immediate help."
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate styles based on position
  const getWidgetStyle = () => {
    if (position) {
      return {
        left: position.x,
        top: position.y,
        right: 'auto',
        bottom: 'auto',
        cursor: isDragging ? 'grabbing' : 'default'
      };
    }
    return {
      right: '24px',
      bottom: '80px'
    };
  };
  
  const getButtonStyle = () => {
    if (position) {
      return {
        left: position.x,
        top: position.y,
        right: 'auto',
        bottom: 'auto'
      };
    }
    return {
      right: '24px',
      bottom: '80px'
    };
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          ref={widgetRef}
          onClick={() => setIsOpen(true)}
          style={getButtonStyle()}
          className="fixed w-16 h-16 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center z-[9998] transition-transform hover:scale-110"
          data-testid="chat-widget-button"
        >
          <MessageCircle className="w-7 h-7" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          ref={widgetRef}
          style={getWidgetStyle()}
          className={`fixed w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col z-[9998] border border-gray-200 overflow-hidden ${
            isDragging ? 'ring-2 ring-blue-400 shadow-2xl' : ''
          }`}
          data-testid="chat-widget"
        >
          {/* Header with drag handle */}
          <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
            {/* Drag handle */}
            <div 
              onMouseDown={handleDragStart}
              onTouchStart={handleDragStart}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-white/20 rounded mr-2"
              title="Drag to move"
            >
              <GripVertical className="w-5 h-5" />
            </div>
            
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">Jiffys Assistant</h3>
                <p className="text-xs text-blue-100">Online • Ready to help</p>
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              {position && (
                <button 
                  onClick={resetPosition}
                  className="p-1 hover:bg-white/20 rounded"
                  title="Reset position"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                  <div className={`p-3 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'bg-blue-600 text-white rounded-br-sm' 
                      : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  
                  {/* Show providers if available */}
                  {msg.providers && msg.providers.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {msg.providers.slice(0, 3).map((provider, i) => (
                        <div key={i} className="bg-white p-3 rounded-lg border border-gray-200 text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                              {provider.picture ? (
                                <img src={provider.picture} alt="" className="w-full h-full rounded-full object-cover" />
                              ) : (
                                <User className="w-4 h-4 text-gray-500" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{provider.name}</p>
                              <p className="text-xs text-gray-500">{provider.vehicle_type || 'Service Vehicle'}</p>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600">
                            ⭐ {provider.rating?.toFixed(1) || '5.0'} • {provider.total_jobs || 0} jobs • ~{provider.eta || '15'} min away
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white p-3 rounded-2xl rounded-bl-sm shadow-sm border border-gray-100">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 bg-white border-t border-gray-200">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything..."
                className="flex-1"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button 
                type="submit" 
                size="icon" 
                className="bg-blue-600 hover:bg-blue-700"
                disabled={loading || !input.trim()}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Or call <a href="tel:2602183536" className="text-blue-600">(260) 218-3536</a> for immediate help
            </p>
          </form>
        </div>
      )}
    </>
  );
}
